import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import MapGL, { Marker, Popup, NavigationControl, Source, Layer, MapRef } from 'react-map-gl/mapbox';
import type { LineLayer } from 'mapbox-gl';
import { MAPBOX_TOKEN } from 'config/Mapbox';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';
import CoverageAnalysisService from 'service/CoverageAnalysisService';
import UserDetailModal from 'component/userDetailModal/UserViewModal';
import { AreaCoverageData, OfficerCoverageData, OfficerActiveProjectItem, ExProjectOption, RulerPoint } from './types';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

const COLORS = {
  primary: '#2B75DA',
  primaryDark: '#1E3A8A',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  grayText: '#64748b',
  darkText: '#1e293b',
  lightBg: '#f8fafc',
  border: '#e2e8f0',
  gradeA: '#10b981',
  gradeB: '#2563eb',
  gradeC: '#f59e0b',
  gradeD: '#ef4444',
  ungraded: '#94a3b8',
};

interface GradePillConfig {
  key: string;
  label: string;
  color: string;
  activeBg: string;
  activeBorder: string;
  activeText: string;
}

const GRADE_PILLS: GradePillConfig[] = [
  {
    key: 'A',
    label: 'Grade A',
    color: '#10b981',
    activeBg: '#ffffff',
    activeBorder: '#10b981',
    activeText: '#047857',
  },
  {
    key: 'B',
    label: 'Grade B',
    color: '#2563eb',
    activeBg: '#ffffff',
    activeBorder: '#3b82f6',
    activeText: '#1d4ed8',
  },
  {
    key: 'C',
    label: 'Grade C',
    color: '#f59e0b',
    activeBg: '#ffffff',
    activeBorder: '#f59e0b',
    activeText: '#b45309',
  },
  {
    key: 'D',
    label: 'Grade D',
    color: '#ef4444',
    activeBg: '#ffffff',
    activeBorder: '#ef4444',
    activeText: '#b91c1c',
  },
];

// Calculate geodesic distance between two points using Haversine formula (in km)
const calculateDistanceKm = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's mean radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Format distance: < 1 km in meters, >= 1 km in kilometers
const formatRulerDistance = (distKm: number): string => {
  if (distKm < 1) {
    const meters = Math.round(distKm * 1000);
    return `${meters} m`;
  }
  return `${distKm.toFixed(2)} km`;
};

const DEFAULT_MAP_VIEW = {
  longitude: 116.5,
  latitude: -2.2,
  zoom: 4.8,
};

// Zoom thresholds for progressive disclosure of city information
const SHOW_CITY_NAME_ZOOM = 7.0;
const SHOW_CITY_DETAILS_ZOOM = 10.0;

const getOfficerProjectInfo = (
  p: string | OfficerActiveProjectItem
): { name: string; taskCount?: number } => {
  if (typeof p === 'string') {
    return { name: p };
  }
  return { name: p?.name || '', taskCount: p?.task_count };
};

const ASSIGNMENT_FILTER_OPTIONS: DropdownOption[] = [
  { id: 'ALL', name: 'All Officers' },
  { id: 'ASSIGNED', name: 'Assigned to Active Projects' },
  { id: 'UNDER_10_TASKS', name: 'Low Workload (< 10 Active Tasks)' },
  { id: 'HEAVY_WORKLOAD', name: 'Heavy Workload (≥ 10 Active Tasks)' },
  { id: 'UNASSIGNED', name: 'No Active Assignment' },
];

const PROJECT_PIE_PALETTE = [
  '#2563eb', // Royal Blue
  '#10b981', // Emerald Green
  '#f59e0b', // Amber
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#14b8a6', // Teal
  '#6366f1', // Indigo
  '#84cc16', // Lime
  '#d946ef', // Fuchsia
  '#3b82f6', // Light Blue
];

const getOrCreateCoverageChartTooltip = () => {
  let tooltipEl = document.getElementById('coverage-chart-tooltip');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'coverage-chart-tooltip';
    tooltipEl.style.position = 'fixed';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.zIndex = '99999';
    tooltipEl.style.background = '#1e293b';
    tooltipEl.style.color = '#ffffff';
    tooltipEl.style.borderRadius = '8px';
    tooltipEl.style.padding = '7px 11px';
    tooltipEl.style.fontSize = '12px';
    tooltipEl.style.boxShadow = '0 6px 16px rgba(0,0,0,0.22)';
    tooltipEl.style.whiteSpace = 'nowrap';
    tooltipEl.style.opacity = '0';
    tooltipEl.style.transition = 'opacity 0.12s ease';
    document.body.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const coverageChartTooltipHandler = (context: any) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateCoverageChartTooltip();

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  if (tooltip.body) {
    const lines = tooltip.body.map((b: any) => b.lines).flat();
    const colors = tooltip.labelColors;

    const rows = lines.map((line: string, i: number) => {
      const bg = colors?.[i]?.backgroundColor || '#94a3b8';
      return `
        <div style="display: flex; align-items: center; gap: 7px;">
          <span style="
            width: 8px; height: 8px; border-radius: 50%;
            background: ${bg}; flex-shrink: 0; display: inline-block;
          "></span>
          <span style="font-weight: 500; font-size: 11.5px; color: #ffffff;">${line}</span>
        </div>
      `;
    });

    tooltipEl.innerHTML = rows.join('');
  }

  const canvasRect = chart.canvas.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();

  let left = canvasRect.left + tooltip.caretX + 12;
  let top = canvasRect.top + tooltip.caretY - tooltipRect.height / 2;

  if (left + tooltipRect.width > window.innerWidth - 12) {
    left = canvasRect.left + tooltip.caretX - tooltipRect.width - 12;
  }
  if (left < 12) left = 12;

  tooltipEl.style.left = left + 'px';
  tooltipEl.style.top = top + 'px';
  tooltipEl.style.opacity = '1';
};

const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 400,
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
      external: coverageChartTooltipHandler,
      callbacks: {
        label: (context: any) => {
          const label = context.label || '';
          const value = Number(context.raw) || 0;
          const total = context.dataset.data.reduce(
            (a: number, b: number) => a + b,
            0
          );
          const percentage =
            total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
          return `${label}: ${value.toLocaleString()} (${percentage}%)`;
        },
      },
    },
  },
};

const CoverageAnalysis: React.FC = () => {
  const authState = useSelector((state: any) => state.auth);
  const token =
    authState?.profile?.data?.token ||
    authState?.profile?.token ||
    authState?.token;

  const coverageService = useMemo(() => CoverageAnalysisService(), []);

  // Data states from API
  const [areas, setAreas] = useState<AreaCoverageData[]>([]);
  const [officers, setOfficers] = useState<OfficerCoverageData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Global dropdown filters
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedExProjects, setSelectedExProjects] = useState<string[]>([]);
  const [selectedAssignmentFilter, setSelectedAssignmentFilter] = useState<string>('ALL');

  // Master ex-projects options from backend
  const [apiExProjects, setApiExProjects] = useState<ExProjectOption[]>([]);

  // Grade filter as interactive toggle pills (default: Grade A, B, and C)
  const [selectedGrades, setSelectedGrades] = useState<string[]>(['A', 'B', 'C']);

  // Selected city/area (null = Overview Mode with city markers, not null = City Detail Mode with officer markers)
  const [selectedArea, setSelectedArea] = useState<AreaCoverageData | null>(null);

  // Search terms for table
  const [officerSearchTerm, setOfficerSearchTerm] = useState<string>('');

  // Pagination states
  const [officerPage, setOfficerPage] = useState<number>(1);
  const [officerPageSize, setOfficerPageSize] = useState<number>(10);

  // Map reference, zoom & popups
  const mapRef = useRef<MapRef | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(DEFAULT_MAP_VIEW.zoom);
  const [hoveredAreaId, setHoveredAreaId] = useState<string | null>(null);
  const [selectedAreaForPopup, setSelectedAreaForPopup] = useState<AreaCoverageData | null>(null);
  const [selectedOfficerForPopup, setSelectedOfficerForPopup] = useState<OfficerCoverageData | null>(null);

  // Highlighting states
  const [highlightedAreaId, setHighlightedAreaId] = useState<string | null>(null);
  const [highlightedOfficerId, setHighlightedOfficerId] = useState<string | null>(null);

  // Field Officer Detail Modal states
  const [selectedOfficerForModal, setSelectedOfficerForModal] = useState<string | null>(null);
  const [showUserDetailModal, setShowUserDetailModal] = useState<boolean>(false);

  // Map Ruler (Fitur Penggaris Jarak) states
  const [isRulerActive, setIsRulerActive] = useState<boolean>(false);
  const [rulerPointA, setRulerPointA] = useState<RulerPoint | null>(null);
  const [rulerPointB, setRulerPointB] = useState<RulerPoint | null>(null);
  const [rulerHoverPoint, setRulerHoverPoint] = useState<RulerPoint | null>(null);

  const handleToggleRuler = useCallback(() => {
    setIsRulerActive((prev) => {
      const next = !prev;
      if (!next) {
        setRulerPointA(null);
        setRulerPointB(null);
        setRulerHoverPoint(null);
      }
      return next;
    });
  }, []);

  const handleResetRuler = useCallback(() => {
    setRulerPointA(null);
    setRulerPointB(null);
    setRulerHoverPoint(null);
  }, []);

  const handleRulerPointSelect = useCallback(
    (lat: number, lng: number) => {
      if (!rulerPointA) {
        setRulerPointA({ lat, lng });
        setRulerHoverPoint(null);
      } else if (!rulerPointB) {
        setRulerPointB({ lat, lng });
        setRulerHoverPoint(null);
      } else {
        // Both A and B were already placed, user clicks to start a new measurement
        setRulerPointA({ lat, lng });
        setRulerPointB(null);
        setRulerHoverPoint(null);
      }
    },
    [rulerPointA, rulerPointB]
  );

  const activeRulerPointB = rulerPointB || (isRulerActive ? rulerHoverPoint : null);

  const rulerGeoJSON: any = useMemo(() => {
    if (!rulerPointA || !activeRulerPointB) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [rulerPointA.lng, rulerPointA.lat],
              [activeRulerPointB.lng, activeRulerPointB.lat],
            ],
          },
          properties: {},
        },
      ],
    };
  }, [rulerPointA, activeRulerPointB]);

  const rulerLineCasingLayer: LineLayer = useMemo(
    () => ({
      id: 'ruler-line-casing',
      type: 'line',
      source: 'ruler-line-source',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#ffffff',
        'line-width': 6,
        'line-opacity': 0.9,
      },
    }),
    []
  );

  const rulerLineLayer: LineLayer = useMemo(
    () => ({
      id: 'ruler-line',
      type: 'line',
      source: 'ruler-line-source',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#2563eb',
        'line-width': 3.5,
        'line-dasharray': [2, 1.5],
      },
    }),
    []
  );

  const handleOpenUserDetailModal = (officerId: string) => {
    setSelectedOfficerForModal(officerId);
    setShowUserDetailModal(true);
  };

  const handleCloseUserDetailModal = () => {
    setShowUserDetailModal(false);
    setSelectedOfficerForModal(null);
  };

  // Fetch coverage analysis data
  const fetchData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const res = await coverageService.getCoverageAnalysis(token);
      if (res && res.data) {
        setAreas(res.data.areas || []);
        setOfficers(res.data.officers || []);
        if (res.data.ex_projects) {
          setApiExProjects(res.data.ex_projects);
        }
      }
    } catch (err: any) {
      console.error('Error fetching Workload & Coverage Analysis:', err);
      setErrorMessage(
        typeof err === 'string'
          ? err
          : 'Failed to load Workload & Coverage Analysis data. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [token, coverageService]);

  useEffect(() => {
    fetchData();
    return () => {
      const el = document.getElementById('coverage-chart-tooltip');
      if (el) {
        el.remove();
      }
    };
  }, [fetchData]);

  // Helper to extract grade category key from grade string
  const getOfficerGradeKey = useCallback((gradeStr: string): string => {
    const g = gradeStr ? gradeStr.toUpperCase().trim() : '';
    if (g.includes('GRADE A') || g === 'A' || g.endsWith(' A')) return 'A';
    if (g.includes('GRADE B') || g === 'B' || g.endsWith(' B')) return 'B';
    if (g.includes('GRADE C') || g === 'C' || g.endsWith(' C')) return 'C';
    if (g.includes('GRADE D') || g === 'D' || g.endsWith(' D')) return 'D';
    return 'UNGRADED';
  }, []);

  // Grade pill toggle handler
  const handleToggleGrade = (key: string) => {
    setSelectedGrades((prev) => {
      if (prev.includes(key)) {
        return prev.filter((item) => item !== key);
      } else {
        return [...prev, key];
      }
    });
    setOfficerPage(1);
  };

  const handleSelectAllGrades = () => {
    if (selectedGrades.length === 4) {
      setSelectedGrades(['A', 'B', 'C']);
    } else {
      setSelectedGrades(['A', 'B', 'C', 'D']);
    }
    setOfficerPage(1);
  };

  // Province dropdown options
  const provinceOptions: DropdownOption[] = useMemo(() => {
    const provSet = new Set<string>();
    areas.forEach((a) => {
      if (a.province_name && a.province_name.trim() !== '') {
        provSet.add(a.province_name);
      }
    });
    return Array.from(provSet)
      .sort()
      .map((p) => ({ id: p, name: p }));
  }, [areas]);

  // City dropdown options (filtered by selected province if any)
  const cityOptions = useMemo(() => {
    return areas
      .filter((a) => {
        if (selectedProvince && a.province_name !== selectedProvince) {
          return false;
        }
        return true;
      })
      .map((a) => ({
        code: a.id,
        name: a.name,
        province: a.province_name,
        description: a.province_name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [areas, selectedProvince]);

  // Ex-project options for dropdown (extracted from backend apiExProjects or officer records)
  const exProjectOptions = useMemo(() => {
    const map = new Map<string, string>();
    apiExProjects.forEach((p) => {
      if (p.id && p.name) map.set(p.id, p.name);
    });
    officers.forEach((o) => {
      o.ex_projects?.forEach((p) => {
        if (p.id && p.name && !map.has(p.id)) {
          map.set(p.id, p.name);
        }
      });
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({
        code: id,
        name: name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [apiExProjects, officers]);

  // Helper to check whether officer belongs to the selected province (direct or via area lookup)
  const isOfficerInSelectedProvince = useCallback(
    (officer: OfficerCoverageData, province: string | null): boolean => {
      if (!province) return true;
      const targetProv = province.trim().toLowerCase();
      if (
        officer.province_name &&
        officer.province_name.trim().toLowerCase() === targetProv
      ) {
        return true;
      }
      // Fallback: match via officer's area_id in areas list
      if (officer.area_id) {
        const officerAreaId = officer.area_id.toLowerCase().trim();
        const found = areas.find(
          (a) => a.id.toLowerCase().trim() === officerAreaId
        );
        if (
          found &&
          found.province_name &&
          found.province_name.trim().toLowerCase() === targetProv
        ) {
          return true;
        }
      }
      // Fallback: match via officer's area_name in areas list
      if (officer.area_name) {
        const officerAreaName = officer.area_name.trim().toLowerCase();
        const found = areas.find(
          (a) => a.name.trim().toLowerCase() === officerAreaName
        );
        if (
          found &&
          found.province_name &&
          found.province_name.trim().toLowerCase() === targetProv
        ) {
          return true;
        }
      }
      return false;
    },
    [areas]
  );

  // Helper to check whether officer belongs to any of the selected cities
  const isOfficerInSelectedCities = useCallback(
    (officer: OfficerCoverageData, cityIds: string[]): boolean => {
      if (!cityIds || cityIds.length === 0) return true;
      const normalizedCityIds = cityIds.map((cId) => cId.toLowerCase().trim());
      if (
        officer.area_id &&
        normalizedCityIds.includes(officer.area_id.toLowerCase().trim())
      ) {
        return true;
      }
      const officerAreaName = officer.area_name ? officer.area_name.toLowerCase().trim() : '';
      if (officerAreaName) {
        return normalizedCityIds.some((cId) => {
          const found = areas.find((a) => a.id.toLowerCase().trim() === cId);
          if (!found) return false;
          const cleanFound = found.name.toLowerCase().trim().replace(/^(kota|kabupaten|kab\.|adm\.)\s+/i, '');
          const cleanOfficer = officerAreaName.replace(/^(kota|kabupaten|kab\.|adm\.)\s+/i, '');
          return cleanFound === cleanOfficer || found.name.toLowerCase().trim() === officerAreaName;
        });
      }
      return false;
    },
    [areas]
  );

  // Helper to check whether officer has worked on any of the selected ex-projects (with at least 1 approved result)
  const isOfficerInSelectedExProjects = useCallback(
    (officer: OfficerCoverageData, exProjectIds: string[]): boolean => {
      if (!exProjectIds || exProjectIds.length === 0) return true;
      if (!officer.ex_projects || officer.ex_projects.length === 0) return false;
      const normalizedIds = exProjectIds.map((pId) => pId.toLowerCase().trim());
      return officer.ex_projects.some((p) =>
        p.id && normalizedIds.includes(p.id.toLowerCase().trim())
      );
    },
    []
  );

  // Filtered areas based on selected province and selected cities
  const filteredAreas = useMemo(() => {
    return areas.filter((a) => {
      if (
        selectedProvince &&
        a.province_name?.trim().toLowerCase() !== selectedProvince.trim().toLowerCase()
      ) {
        return false;
      }
      if (
        selectedCities.length > 0 &&
        !selectedCities.some((cId) => cId.toLowerCase().trim() === a.id.toLowerCase().trim())
      ) {
        return false;
      }
      return true;
    });
  }, [areas, selectedProvince, selectedCities]);

  // Filtered officers based on province, selected cities, ex-projects, assignment status, grade pills, and search
  const filteredOfficers = useMemo(() => {
    return officers.filter((o) => {
      if (!isOfficerInSelectedProvince(o, selectedProvince)) {
        return false;
      }

      if (!isOfficerInSelectedCities(o, selectedCities)) {
        return false;
      }

      if (!isOfficerInSelectedExProjects(o, selectedExProjects)) {
        return false;
      }

      // Assignment & workload filter: assigned officers partition strictly into (< 10) and (>= 10) tasks
      const isAssigned = Boolean(
        o.is_assigned ||
        (o.active_projects && o.active_projects.length > 0) ||
        (o.active_task_count && o.active_task_count > 0)
      );
      const taskCount = Number(o.active_task_count) || 0;

      if (selectedAssignmentFilter === 'ASSIGNED') {
        if (!isAssigned) return false;
      } else if (selectedAssignmentFilter === 'UNDER_10_TASKS') {
        if (!isAssigned || taskCount >= 10) return false;
      } else if (selectedAssignmentFilter === 'HEAVY_WORKLOAD') {
        if (!isAssigned || taskCount < 10) return false;
      } else if (selectedAssignmentFilter === 'UNASSIGNED') {
        if (isAssigned) return false;
      }

      // Grade filter: only allow Grade A - D
      const gradeKey = getOfficerGradeKey(o.grade);
      if (gradeKey === 'UNGRADED' || !selectedGrades.includes(gradeKey)) {
        return false;
      }
      if (officerSearchTerm.trim() !== '') {
        const query = officerSearchTerm.toLowerCase().trim();
        const matchesName = o.name.toLowerCase().includes(query);
        const matchesEmail = o.email.toLowerCase().includes(query);
        const matchesPhone = o.phone.toLowerCase().includes(query);
        const matchesArea = o.area_name.toLowerCase().includes(query);
        const matchesProv = o.province_name.toLowerCase().includes(query);
        const matchesGrade = o.grade.toLowerCase().includes(query);
        const matchesPrj = o.active_projects.some((p) => {
          const prjName = typeof p === 'string' ? p : p.name;
          return prjName.toLowerCase().includes(query);
        });
        const matchesExPrj = Boolean(
          o.ex_projects &&
          o.ex_projects.some((p) => p.name.toLowerCase().includes(query))
        );
        if (
          !matchesName &&
          !matchesEmail &&
          !matchesPhone &&
          !matchesArea &&
          !matchesProv &&
          !matchesGrade &&
          !matchesPrj &&
          !matchesExPrj
        ) {
          return false;
        }
      }
      return true;
    });
  }, [
    officers,
    selectedProvince,
    selectedCities,
    selectedExProjects,
    selectedAssignmentFilter,
    selectedGrades,
    officerSearchTerm,
    getOfficerGradeKey,
    isOfficerInSelectedProvince,
    isOfficerInSelectedCities,
    isOfficerInSelectedExProjects,
  ]);

  // Officers belonging to the currently selected area
  const officersInSelectedArea = useMemo(() => {
    if (!selectedArea) return [];
    return filteredOfficers.filter((o) => {
      if (o.area_id && selectedArea.id) {
        return o.area_id.toLowerCase() === selectedArea.id.toLowerCase();
      }
      return (
        o.area_name.toLowerCase().trim() === selectedArea.name.toLowerCase().trim()
      );
    });
  }, [selectedArea, filteredOfficers]);

  // Distribute officer marker coordinates in circular pattern if overlapping with city center
  const officersWithDisplayCoords = useMemo(() => {
    if (!selectedArea) return [];
    const total = officersInSelectedArea.length;
    return officersInSelectedArea.map((officer, index) => {
      let lat = officer.latitude;
      let lng = officer.longitude;

      const isSameAsArea =
        !lat ||
        !lng ||
        (Math.abs(lat - selectedArea.latitude) < 0.0001 &&
          Math.abs(lng - selectedArea.longitude) < 0.0001);

      if (isSameAsArea && total > 1) {
        const ring = Math.floor(index / 8);
        const angle = ((index % 8) / 8) * 2 * Math.PI + ring * 0.4;
        const radius = 0.006 + ring * 0.005;
        lat = selectedArea.latitude + radius * Math.sin(angle);
        lng = selectedArea.longitude + radius * Math.cos(angle) * 1.15;
      } else if (!lat || !lng) {
        lat = selectedArea.latitude;
        lng = selectedArea.longitude;
      }

      return {
        ...officer,
        displayLat: lat,
        displayLng: lng,
      };
    });
  }, [selectedArea, officersInSelectedArea]);

  // Officers shown in Table 2: If an area is selected, show only officers in that area; otherwise all filtered officers
  const tableOfficers = useMemo(() => {
    if (selectedArea) {
      return officersInSelectedArea;
    }
    return filteredOfficers;
  }, [selectedArea, officersInSelectedArea, filteredOfficers]);

  // Counts of officers per grade category for pill badges
  const gradeCounts = useMemo(() => {
    const counts: Record<string, number> = { A: 0, B: 0, C: 0, D: 0 };
    officers.forEach((o) => {
      if (!isOfficerInSelectedProvince(o, selectedProvince)) return;
      if (!isOfficerInSelectedCities(o, selectedCities)) return;
      if (!isOfficerInSelectedExProjects(o, selectedExProjects)) return;

      // Assignment & workload filter: assigned officers partition strictly into (< 10) and (>= 10) tasks
      const isAssigned = Boolean(
        o.is_assigned ||
        (o.active_projects && o.active_projects.length > 0) ||
        (o.active_task_count && o.active_task_count > 0)
      );
      const taskCount = Number(o.active_task_count) || 0;

      if (selectedAssignmentFilter === 'ASSIGNED') {
        if (!isAssigned) return;
      } else if (selectedAssignmentFilter === 'UNDER_10_TASKS') {
        if (!isAssigned || taskCount >= 10) return;
      } else if (selectedAssignmentFilter === 'HEAVY_WORKLOAD') {
        if (!isAssigned || taskCount < 10) return;
      } else if (selectedAssignmentFilter === 'UNASSIGNED') {
        if (isAssigned) return;
      }

      const k = getOfficerGradeKey(o.grade);
      if (k in counts) {
        counts[k]++;
      }
    });
    return counts;
  }, [
    officers,
    selectedProvince,
    selectedCities,
    selectedExProjects,
    selectedAssignmentFilter,
    getOfficerGradeKey,
    isOfficerInSelectedProvince,
    isOfficerInSelectedCities,
    isOfficerInSelectedExProjects,
  ]);

  // Dynamic breakdown stats per area respecting active grade pills, province, and project filters
  const areaFilteredStatsMap = useMemo(() => {
    const map = new Map<
      string,
      {
        total: number;
        gradeA: number;
        gradeB: number;
        gradeC: number;
        gradeD: number;
      }
    >();

    areas.forEach((a) => {
      map.set(a.id.toLowerCase().trim(), {
        total: 0,
        gradeA: 0,
        gradeB: 0,
        gradeC: 0,
        gradeD: 0,
      });
    });

    filteredOfficers.forEach((o) => {
      let aId = o.area_id ? o.area_id.toLowerCase().trim() : '';
      if (!aId && o.area_name) {
        const found = areas.find(
          (a) => a.name.toLowerCase().trim() === o.area_name.toLowerCase().trim()
        );
        if (found) aId = found.id.toLowerCase().trim();
      }

      if (aId && map.has(aId)) {
        const item = map.get(aId)!;
        item.total++;
        const gKey = getOfficerGradeKey(o.grade);
        if (gKey === 'A') item.gradeA++;
        else if (gKey === 'B') item.gradeB++;
        else if (gKey === 'C') item.gradeC++;
        else if (gKey === 'D') item.gradeD++;
      }
    });

    return map;
  }, [areas, filteredOfficers, getOfficerGradeKey]);

  // Base officers for workload and project distribution pie charts (respects Province, Selected Cities, ex-Projects, Assignment, Grade, and City Focus)
  const chartBaseOfficers = useMemo(() => {
    return officers.filter((o) => {
      if (!isOfficerInSelectedProvince(o, selectedProvince)) {
        return false;
      }
      if (!isOfficerInSelectedCities(o, selectedCities)) {
        return false;
      }
      if (!isOfficerInSelectedExProjects(o, selectedExProjects)) {
        return false;
      }

      // Assignment & workload filter: assigned officers partition strictly into (< 10) and (>= 10) tasks
      const isAssigned = Boolean(
        o.is_assigned ||
        (o.active_projects && o.active_projects.length > 0) ||
        (o.active_task_count && o.active_task_count > 0)
      );
      const taskCount = Number(o.active_task_count) || 0;

      if (selectedAssignmentFilter === 'ASSIGNED') {
        if (!isAssigned) return false;
      } else if (selectedAssignmentFilter === 'UNDER_10_TASKS') {
        if (!isAssigned || taskCount >= 10) return false;
      } else if (selectedAssignmentFilter === 'HEAVY_WORKLOAD') {
        if (!isAssigned || taskCount < 10) return false;
      } else if (selectedAssignmentFilter === 'UNASSIGNED') {
        if (isAssigned) return false;
      }

      if (selectedArea) {
        if (o.area_id && selectedArea.id) {
          if (o.area_id.toLowerCase() !== selectedArea.id.toLowerCase()) return false;
        } else if (
          o.area_name.toLowerCase().trim() !== selectedArea.name.toLowerCase().trim()
        ) {
          return false;
        }
      }
      const gradeKey = getOfficerGradeKey(o.grade);
      if (gradeKey === 'UNGRADED' || !selectedGrades.includes(gradeKey)) {
        return false;
      }
      return true;
    });
  }, [
    officers,
    selectedProvince,
    selectedCities,
    selectedExProjects,
    selectedAssignmentFilter,
    selectedArea,
    selectedGrades,
    getOfficerGradeKey,
    isOfficerInSelectedProvince,
    isOfficerInSelectedCities,
    isOfficerInSelectedExProjects,
  ]);

  // 1. Workload status breakdown (Low Workload, Heavy Workload, No Active Assignment)
  const workloadChartData = useMemo(() => {
    let lowWorkload = 0;
    let heavyWorkload = 0;
    let noActiveAssignment = 0;

    chartBaseOfficers.forEach((o) => {
      const isAssigned = Boolean(
        o.is_assigned ||
        (o.active_projects && o.active_projects.length > 0) ||
        (o.active_task_count && o.active_task_count > 0)
      );
      const taskCount = Number(o.active_task_count) || 0;

      if (!isAssigned) {
        noActiveAssignment++;
      } else if (taskCount >= 10) {
        heavyWorkload++;
      } else {
        lowWorkload++;
      }
    });

    const total = chartBaseOfficers.length;

    const items = [
      {
        label: 'Low Workload (< 10 Tasks)',
        count: lowWorkload,
        percentage: total > 0 ? Number(((lowWorkload / total) * 100).toFixed(1)) : 0,
        color: '#0ea5e9',
      },
      {
        label: 'Heavy Workload (≥ 10 Tasks)',
        count: heavyWorkload,
        percentage: total > 0 ? Number(((heavyWorkload / total) * 100).toFixed(1)) : 0,
        color: '#6366f1',
      },
      {
        label: 'No Active Assignment',
        count: noActiveAssignment,
        percentage: total > 0 ? Number(((noActiveAssignment / total) * 100).toFixed(1)) : 0,
        color: '#94a3b8',
      },
    ];

    return {
      total,
      items,
      chartData: {
        labels: items.map((i) => i.label),
        datasets: [
          {
            data: items.map((i) => i.count),
            backgroundColor: items.map((i) => i.color),
            borderColor: '#ffffff',
            borderWidth: 2,
          },
        ],
      },
    };
  }, [chartBaseOfficers]);

  // 2. Active projects vs No Active Assignment breakdown
  // (If an officer works on >1 project, count 1 for each project)
  const projectChartData = useMemo(() => {
    const projectCounts: Record<string, number> = {};
    let unassignedCount = 0;

    chartBaseOfficers.forEach((o) => {
      const isAssigned = Boolean(
        o.is_assigned ||
        (o.active_projects && o.active_projects.length > 0) ||
        (o.active_task_count && o.active_task_count > 0)
      );

      if (!isAssigned) {
        unassignedCount++;
      } else {
        if (o.active_projects && o.active_projects.length > 0) {
          o.active_projects.forEach((p) => {
            const pName = getOfficerProjectInfo(p).name.trim();
            if (pName) {
              projectCounts[pName] = (projectCounts[pName] || 0) + 1;
            }
          });
        } else {
          projectCounts['Other Active Tasks'] = (projectCounts['Other Active Tasks'] || 0) + 1;
        }
      }
    });

    const sortedProjects = Object.entries(projectCounts).sort((a, b) => b[1] - a[1]);

    // Display top 5 projects, map the remaining projects into "Other Projects"
    const top5Projects = sortedProjects.slice(0, 5);
    const otherProjects = sortedProjects.slice(5);

    const items: Array<{
      label: string;
      count: number;
      percentage: number;
      color: string;
      tooltipDesc?: string;
    }> = [];

    top5Projects.forEach(([pName, count], idx) => {
      items.push({
        label: pName,
        count,
        percentage: 0,
        color: PROJECT_PIE_PALETTE[idx % PROJECT_PIE_PALETTE.length],
      });
    });

    if (otherProjects.length > 0) {
      const otherCount = otherProjects.reduce((sum, [, count]) => sum + count, 0);
      const otherProjectNames = otherProjects
        .map(([name, count]) => `${name} (${count})`)
        .join(', ');

      items.push({
        label: 'Other Projects',
        count: otherCount,
        percentage: 0,
        color: '#64748b',
        tooltipDesc: `${otherProjects.length} other projects: ${otherProjectNames}`,
      });
    }

    // Add No Active Assignment slice at the end
    items.push({
      label: 'No Active Assignment',
      count: unassignedCount,
      percentage: 0,
      color: '#94a3b8',
    });

    const total = items.reduce((sum, item) => sum + item.count, 0);

    items.forEach((item) => {
      item.percentage = total > 0 ? Number(((item.count / total) * 100).toFixed(1)) : 0;
    });

    return {
      total,
      uniqueOfficers: chartBaseOfficers.length,
      items,
      chartData: {
        labels: items.map((i) => i.label),
        datasets: [
          {
            data: items.map((i) => i.count),
            backgroundColor: items.map((i) => i.color),
            borderColor: '#ffffff',
            borderWidth: 2,
          },
        ],
      },
    };
  }, [chartBaseOfficers]);

  // Paginated officers
  const totalOfficerRecords = tableOfficers.length;
  const totalOfficerPages = Math.ceil(totalOfficerRecords / officerPageSize) || 1;
  const paginatedOfficers = useMemo(() => {
    const start = (officerPage - 1) * officerPageSize;
    return tableOfficers.slice(start, start + officerPageSize);
  }, [tableOfficers, officerPage, officerPageSize]);

  // Reset pagination when tableOfficers changes length
  useEffect(() => {
    setOfficerPage(1);
  }, [selectedArea]);

  // Total summary metrics
  const summaryMetrics = useMemo(() => {
    const totalAreasCount = areas.length;
    let gradedCount = 0;
    officers.forEach((o) => {
      const g = getOfficerGradeKey(o.grade);
      if (g !== 'UNGRADED') {
        gradedCount++;
      }
    });

    const activePrjSet = new Set<string>();
    areas.forEach((a) => {
      a.active_projects.forEach((p) => activePrjSet.add(p.name));
    });

    return {
      totalAreas: totalAreasCount,
      totalOfficers: gradedCount,
      gradedOfficers: gradedCount,
      totalActiveProjects: activePrjSet.size,
    };
  }, [areas, officers, getOfficerGradeKey]);

  // Fly to location helper (smoothly pans to coordinate, only changes zoom if zoomLevel is provided)
  const flyToLocation = (lat: number, lng: number, zoomLevel?: number) => {
    if (!lat || !lng || !mapRef.current) return;
    mapRef.current.flyTo({
      center: [lng, lat],
      ...(zoomLevel !== undefined ? { zoom: zoomLevel } : {}),
      duration: 600,
    });
  };

  // City selection handler (switches to City Detail Mode)
  const handleSelectCity = (area: AreaCoverageData) => {
    setSelectedArea(area);
    setHighlightedAreaId(area.id);
    setSelectedOfficerForPopup(null);
    setSelectedAreaForPopup(null);
    if (area.latitude && area.longitude) {
      flyToLocation(area.latitude, area.longitude, SHOW_CITY_DETAILS_ZOOM);
    }
  };

  // Double click handler for city markers
  const lastAreaClickRef = useRef<{ id: string; time: number } | null>(null);
  const handleAreaClick = (area: AreaCoverageData) => {
    const now = Date.now();
    if (
      lastAreaClickRef.current &&
      lastAreaClickRef.current.id === area.id &&
      now - lastAreaClickRef.current.time < 350
    ) {
      // Double click detected: immediately show officer distribution
      lastAreaClickRef.current = null;
      handleSelectCity(area);
    } else {
      // Single click: action removed as requested
      lastAreaClickRef.current = { id: area.id, time: now };
    }
  };

  // Back to All Cities handler (switches back to Overview Mode without changing camera position)
  const handleBackToAllCities = () => {
    setSelectedArea(null);
    setSelectedAreaForPopup(null);
    setSelectedOfficerForPopup(null);
    setHighlightedAreaId(null);
    setHighlightedOfficerId(null);
  };

  // Grade badge styling helper
  const renderGradeBadge = (grade: string) => {
    const g = grade ? grade.toUpperCase().trim() : '';
    if (g.includes('GRADE A') || g === 'A') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: COLORS.gradeA,
            color: '#ffffff',
            fontSize: '11px',
            padding: '4px 8px',
          }}
        >
          Grade A
        </span>
      );
    }
    if (g.includes('GRADE B') || g === 'B') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: COLORS.gradeB,
            color: '#ffffff',
            fontSize: '11px',
            padding: '4px 8px',
          }}
        >
          Grade B
        </span>
      );
    }
    if (g.includes('GRADE C') || g === 'C') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: COLORS.gradeC,
            color: '#ffffff',
            fontSize: '11px',
            padding: '4px 8px',
          }}
        >
          Grade C
        </span>
      );
    }
    if (g.includes('GRADE D') || g === 'D') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: COLORS.gradeD,
            color: '#ffffff',
            fontSize: '11px',
            padding: '4px 8px',
          }}
        >
          Grade D
        </span>
      );
    }
    return (
      <span
        className="badge bg-light text-muted border"
        style={{ fontSize: '11px', padding: '4px 8px' }}
      >
        Ungraded
      </span>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="row g-3">
        {/* Page Header */}
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div>
              <h5 className="mb-0 fw-bold">Workload & Coverage Analysis</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                onClick={fetchData}
                disabled={isLoading}
                style={{ borderRadius: '8px', padding: '6px 14px' }}
              >
                <i className={`fa-solid fa-arrows-rotate ${isLoading ? 'fa-spin' : ''}`}></i>
                Refresh Data
              </button>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="col-12">
            <div
              className="alert alert-danger d-flex align-items-center justify-content-between"
              role="alert"
            >
              <div>
                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                {errorMessage}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={fetchData}
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Main Content Box */}
        <div className="col-12">
          <div
            className="p-4 bg-white rounded-3 shadow-sm border"
            style={{ borderColor: COLORS.border }}
          >
            {/* Top Filter Bar */}
            <div className="mb-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                {/* Left Controls: Dropdowns & Reset */}
                <div className="d-flex flex-wrap align-items-center gap-2">
                  {/* Province Filter */}
                  <div style={{ width: 210, height: 44 }}>
                    <CustomDropdown
                      value={selectedProvince}
                      options={provinceOptions}
                      showDefaultOption={true}
                      defaultOptionLabel="All Provinces"
                      searchable={true}
                      searchPlaceholder="Search province..."
                      onChange={(opt) => {
                        const newProv =
                          opt.id != null && opt.id !== '' ? String(opt.id) : null;
                        setSelectedProvince(newProv);
                        if (newProv) {
                          setSelectedCities((prev) =>
                            prev.filter((cId) => {
                              const a = areas.find((item) => item.id.toLowerCase().trim() === cId.toLowerCase().trim());
                              return (
                                a &&
                                a.province_name &&
                                a.province_name.trim().toLowerCase() === newProv.trim().toLowerCase()
                              );
                            })
                          );
                        }
                        handleBackToAllCities();
                        setOfficerPage(1);
                      }}
                      containerStyle={{ width: 210 }}
                      buttonStyle={{ width: 210, height: 44 }}
                    />
                  </div>

                  {/* City Multi-Select Filter */}
                  <div style={{ width: 210, height: 44 }}>
                    <MultiSelectDropdown
                      options={cityOptions}
                      subKey="province"
                      searchKeys={['name', 'province']}
                      placeholder={selectedCities.length === 0 ? "All Cities" : "Select Cities..."}
                      allSelectedText="All Cities"
                      value={selectedCities}
                      onChange={(selected) => {
                        setSelectedCities(selected);
                        handleBackToAllCities();
                        setOfficerPage(1);
                      }}
                      stayOpenOnUpdate={true}
                      style={{ width: 210 }}
                      btnClassName="btn btn-outline-secondary d-flex align-items-center justify-content-between gap-2 px-3 py-2 text-dark bg-white border rounded-3 transition-all"
                      buttonStyle={{ width: 210, height: 44, borderRadius: '8px', fontSize: '13px' }}
                      dropdownStyle={{ minWidth: 260 }}
                    />
                  </div>

                  {/* ex-Project Multi-Select Filter */}
                  <div style={{ width: 210, height: 44 }}>
                    <MultiSelectDropdown
                      options={exProjectOptions}
                      searchKeys={['name']}
                      placeholder={selectedExProjects.length === 0 ? "All ex-Projects" : "Select ex-Projects..."}
                      allSelectedText="All ex-Projects"
                      value={selectedExProjects}
                      onChange={(selected) => {
                        setSelectedExProjects(selected);
                        handleBackToAllCities();
                        setOfficerPage(1);
                      }}
                      stayOpenOnUpdate={true}
                      style={{ width: 210 }}
                      btnClassName="btn btn-outline-secondary d-flex align-items-center justify-content-between gap-2 px-3 py-2 text-dark bg-white border rounded-3 transition-all"
                      buttonStyle={{ width: 210, height: 44, borderRadius: '8px', fontSize: '13px' }}
                      dropdownStyle={{ minWidth: 260 }}
                    />
                  </div>

                  {/* Assignment Status Filter */}
                  <div style={{ width: 230, height: 44 }}>
                    <CustomDropdown
                      value={selectedAssignmentFilter}
                      options={ASSIGNMENT_FILTER_OPTIONS}
                      showDefaultOption={false}
                      onChange={(opt) => {
                        setSelectedAssignmentFilter(String(opt.id || 'ALL'));
                        handleBackToAllCities();
                        setOfficerPage(1);
                      }}
                      containerStyle={{ width: 230 }}
                      buttonStyle={{ width: 230, height: 44 }}
                    />
                  </div>

                  {/* Reset Filters Button */}
                  {(selectedProvince ||
                    selectedCities.length > 0 ||
                    selectedExProjects.length > 0 ||
                    selectedAssignmentFilter !== 'ALL' ||
                    selectedGrades.length !== 3 ||
                    !selectedGrades.includes('A') ||
                    !selectedGrades.includes('B') ||
                    !selectedGrades.includes('C') ||
                    selectedArea !== null) && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary d-flex align-items-center gap-1.5 px-3"
                        style={{ height: 44, borderRadius: '8px', fontSize: '13px' }}
                        onClick={() => {
                          setSelectedProvince(null);
                          setSelectedCities([]);
                          setSelectedExProjects([]);
                          setSelectedAssignmentFilter('ALL');
                          setSelectedGrades(['A', 'B', 'C']);
                          handleBackToAllCities();
                          setOfficerPage(1);
                        }}
                      >
                        <i className="fa-solid fa-rotate-left"></i>
                        Reset Filters
                      </button>
                    )}
                </div>

                {/* Right: Summary Metrics */}
                <div
                  className="d-flex align-items-center flex-wrap gap-2 text-muted"
                  style={{ fontSize: '0.85rem' }}
                >
                  <span>
                    Total Areas:{' '}
                    <strong className="text-dark">{summaryMetrics.totalAreas}</strong>
                  </span>
                  <span>&bull;</span>
                  <span>
                    Field Officers:{' '}
                    <strong className="text-dark">
                      {summaryMetrics.totalOfficers}
                    </strong>{' '}
                    (
                    <span className="text-success fw-bold">
                      {summaryMetrics.gradedOfficers} Graded
                    </span>
                    )
                  </span>
                </div>
              </div>

              {/* Grade Filter Tab Pills Row */}
              <div
                className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3 pt-3 border-top"
                style={{ borderColor: '#f1f5f9', backgroundColor: 'transparent' }}
              >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      color: '#64748b',
                      marginRight: '4px',
                    }}
                  >
                    <i
                      className="fa-solid fa-filter text-primary"
                      style={{ marginRight: '6px', fontSize: '12px' }}
                    ></i>
                    Grade Filter:
                  </div>

                  {/* All Grades Pill Button */}
                  <button
                    type="button"
                    onClick={handleSelectAllGrades}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '34px',
                      padding: '0 14px',
                      borderRadius: '20px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      backgroundColor:
                        selectedGrades.length === 4 ? '#2B75DA' : '#ffffff',
                      color:
                        selectedGrades.length === 4 ? '#ffffff' : '#475569',
                      border:
                        selectedGrades.length === 4
                          ? '1.5px solid #2B75DA'
                          : '1px solid #cbd5e1',
                      boxShadow:
                        selectedGrades.length === 4
                          ? '0 2px 6px rgba(43, 117, 218, 0.28)'
                          : 'none',
                      transition: 'all 0.15s ease',
                      cursor: 'pointer',
                      outline: 'none',
                    }}
                  >
                    <span>All Grades</span>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 700,
                        marginLeft: '8px',
                        padding: '2px 7px',
                        borderRadius: '10px',
                        backgroundColor:
                          selectedGrades.length === 4
                            ? 'rgba(255, 255, 255, 0.25)'
                            : '#ffffff',
                        border:
                          selectedGrades.length === 4
                            ? 'none'
                            : '1px solid #e2e8f0',
                        color:
                          selectedGrades.length === 4 ? '#ffffff' : '#64748b',
                      }}
                    >
                      {((gradeCounts['A'] || 0) + (gradeCounts['B'] || 0) + (gradeCounts['C'] || 0) + (gradeCounts['D'] || 0)).toLocaleString()}
                    </span>
                  </button>

                  {/* Individual Grade Pills (A to D) */}
                  {GRADE_PILLS.map((pill) => {
                    const isActive = selectedGrades.includes(pill.key);
                    const count = gradeCounts[pill.key] || 0;

                    return (
                      <button
                        key={pill.key}
                        type="button"
                        onClick={() => handleToggleGrade(pill.key)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '34px',
                          padding: '0 12px',
                          borderRadius: '20px',
                          fontSize: '12.5px',
                          fontWeight: 600,
                          backgroundColor: '#ffffff',
                          color: isActive ? pill.activeText : '#94a3b8',
                          border: isActive
                            ? `1.5px solid ${pill.activeBorder}`
                            : '1px dashed #cbd5e1',
                          boxShadow: isActive
                            ? `0 2px 6px ${pill.color}20`
                            : 'none',
                          opacity: isActive ? 1 : 0.6,
                          transition: 'all 0.15s ease',
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                      >
                        {/* Status Dot */}
                        <span
                          style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            backgroundColor: isActive ? pill.color : '#cbd5e1',
                            marginRight: '7px',
                            flexShrink: 0,
                          }}
                        />
                        <span>{pill.label}</span>
                        {/* Real-time Officer Count Badge */}
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 700,
                            marginLeft: '8px',
                            padding: '2px 7px',
                            borderRadius: '10px',
                            backgroundColor: isActive
                              ? `${pill.color}18`
                              : '#ffffff',
                            border: isActive
                              ? 'none'
                              : '1px solid #e2e8f0',
                            color: isActive ? pill.activeText : '#94a3b8',
                          }}
                        >
                          {count.toLocaleString()}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Status indicator about map pin mode */}
                <div style={{ fontSize: '12px' }}>
                  {selectedArea ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '5px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 600,
                        backgroundColor: '#eff6ff',
                        color: '#2B75DA',
                        border: '1px solid #bfdbfe',
                      }}
                    >
                      <i className="fa-solid fa-map-pin"></i>
                      <span>
                        City Focus: <strong>{selectedArea.name}</strong> (
                        {officersInSelectedArea.length} Officers)
                      </span>
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '5px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: '#ffffff',
                        color: '#64748b',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <i className="fa-solid fa-globe" style={{ color: '#2B75DA' }}></i>
                      <span>
                        Overview Mode: Showing <strong>{filteredAreas.length}</strong> Cities
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* SECTION: WORKLOAD & PROJECT DISTRIBUTION PIE CHARTS */}
            <div className="row g-3 mb-4">
              {/* Pie Chart 1: Workload Status Breakdown */}
              <div className="col-12 col-lg-6">
                <div
                  className="card border rounded-3 p-3 h-100 shadow-sm"
                  style={{
                    backgroundColor: '#ffffff',
                    borderColor: '#e2e8f0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            backgroundColor: '#e0f2fe',
                            color: '#0284c7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                          }}
                        >
                          <i className="fa-solid fa-chart-pie"></i>
                        </div>
                        <h6
                          className="mb-0 fw-bold"
                          style={{ color: '#1e293b', fontSize: '13.5px' }}
                        >
                          Workload Status Breakdown
                        </h6>
                      </div>
                      <p
                        className="mb-0 text-muted"
                        style={{ fontSize: '11px', marginTop: '2px' }}
                      >
                        Active task distribution{' '}
                        {selectedArea
                          ? `in ${selectedArea.name}`
                          : selectedCities.length > 0
                            ? `in ${selectedCities.length} selected ${selectedCities.length === 1 ? 'city' : 'cities'}`
                            : selectedProvince
                              ? `in ${selectedProvince}`
                              : 'nationwide'}
                      </p>
                    </div>
                    <span
                      className="badge bg-light text-dark border"
                      style={{ fontSize: '11px', padding: '5px 9px' }}
                    >
                      {workloadChartData.total.toLocaleString()} Officers
                    </span>
                  </div>

                  {workloadChartData.total === 0 ? (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center text-muted py-4"
                      style={{ minHeight: '180px' }}
                    >
                      <i
                        className="fa-solid fa-chart-pie mb-2"
                        style={{ fontSize: '28px', color: '#cbd5e1' }}
                      ></i>
                      <span style={{ fontSize: '12px' }}>
                        No officer data found for current filter
                      </span>
                    </div>
                  ) : (
                    <div className="row align-items-center g-3 my-auto">
                      <div className="col-12 col-sm-5 d-flex justify-content-center">
                        <div
                          style={{
                            width: '160px',
                            height: '160px',
                            position: 'relative',
                          }}
                        >
                          <Pie
                            data={workloadChartData.chartData}
                            options={pieChartOptions}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-sm-7">
                        <div className="d-flex flex-column gap-2">
                          {workloadChartData.items.map((item, idx) => (
                            <div
                              key={idx}
                              className="p-2 rounded-2"
                              style={{
                                backgroundColor: '#f8fafc',
                                border: '1px solid #f1f5f9',
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                  <span
                                    style={{
                                      width: '9px',
                                      height: '9px',
                                      borderRadius: '50%',
                                      backgroundColor: item.color,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    className="fw-semibold text-truncate"
                                    style={{
                                      fontSize: '11.5px',
                                      color: '#334155',
                                    }}
                                    title={item.label}
                                  >
                                    {item.label}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                  <span
                                    className="fw-bold"
                                    style={{
                                      fontSize: '11.5px',
                                      color: '#1e293b',
                                    }}
                                  >
                                    {item.count.toLocaleString()}
                                  </span>
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: '10.5px' }}
                                  >
                                    ({item.percentage}%)
                                  </span>
                                </div>
                              </div>
                              <div
                                style={{
                                  width: '100%',
                                  height: '4px',
                                  borderRadius: '2px',
                                  backgroundColor: '#e2e8f0',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    width: `${item.percentage}%`,
                                    height: '100%',
                                    backgroundColor: item.color,
                                    borderRadius: '2px',
                                    transition: 'width 0.3s ease',
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Pie Chart 2: Running Projects & Unassigned Breakdown */}
              <div className="col-12 col-lg-6">
                <div
                  className="card border rounded-3 p-3 h-100 shadow-sm"
                  style={{
                    backgroundColor: '#ffffff',
                    borderColor: '#e2e8f0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            backgroundColor: '#eef2ff',
                            color: '#4f46e5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                          }}
                        >
                          <i className="fa-solid fa-diagram-project"></i>
                        </div>
                        <h6
                          className="mb-0 fw-bold"
                          style={{ color: '#1e293b', fontSize: '13.5px' }}
                        >
                          Project Assignment Breakdown
                        </h6>
                      </div>
                      <p
                        className="mb-0 text-muted"
                        style={{ fontSize: '11px', marginTop: '2px' }}
                      >
                        Running project distribution vs unassigned{' '}
                        {selectedArea
                          ? `in ${selectedArea.name}`
                          : selectedCities.length > 0
                            ? `in ${selectedCities.length} selected ${selectedCities.length === 1 ? 'city' : 'cities'}`
                            : selectedProvince
                              ? `in ${selectedProvince}`
                              : 'nationwide'}
                      </p>
                    </div>
                    <span
                      className="badge bg-light text-dark border"
                      style={{ fontSize: '11px', padding: '5px 9px' }}
                    >
                      {projectChartData.uniqueOfficers.toLocaleString()} Officers
                    </span>
                  </div>

                  {projectChartData.total === 0 ? (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center text-muted py-4"
                      style={{ minHeight: '180px' }}
                    >
                      <i
                        className="fa-solid fa-diagram-project mb-2"
                        style={{ fontSize: '28px', color: '#cbd5e1' }}
                      ></i>
                      <span style={{ fontSize: '12px' }}>
                        No project assignment data found
                      </span>
                    </div>
                  ) : (
                    <div className="row align-items-center g-3 my-auto">
                      <div className="col-12 col-sm-5 d-flex justify-content-center">
                        <div
                          style={{
                            width: '160px',
                            height: '160px',
                            position: 'relative',
                          }}
                        >
                          <Pie
                            data={projectChartData.chartData}
                            options={pieChartOptions}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-sm-7">
                        <div
                          className="d-flex flex-column gap-2"
                          style={{
                            maxHeight: '175px',
                            overflowY: 'auto',
                            paddingRight: '4px',
                          }}
                        >
                          {projectChartData.items.map((item, idx) => (
                            <div
                              key={idx}
                              className="p-2 rounded-2"
                              style={{
                                backgroundColor: '#f8fafc',
                                border: '1px solid #f1f5f9',
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                  <span
                                    style={{
                                      width: '9px',
                                      height: '9px',
                                      borderRadius: '50%',
                                      backgroundColor: item.color,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    className="fw-semibold text-truncate"
                                    style={{
                                      fontSize: '11.5px',
                                      color: '#334155',
                                    }}
                                    title={item.label}
                                  >
                                    {item.label}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                  <span
                                    className="fw-bold"
                                    style={{
                                      fontSize: '11.5px',
                                      color: '#1e293b',
                                    }}
                                  >
                                    {item.count.toLocaleString()}
                                  </span>
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: '10.5px' }}
                                  >
                                    ({item.percentage}%)
                                  </span>
                                </div>
                              </div>
                              <div
                                style={{
                                  width: '100%',
                                  height: '4px',
                                  borderRadius: '2px',
                                  backgroundColor: '#e2e8f0',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    width: `${item.percentage}%`,
                                    height: '100%',
                                    backgroundColor: item.color,
                                    borderRadius: '2px',
                                    transition: 'width 0.3s ease',
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        <div
                          className="text-muted mt-2"
                          style={{ fontSize: '10px', fontStyle: 'italic' }}
                        >
                          *Officers assigned to multiple projects are counted in each project.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 1: MAPS (INTERACTIVE MAPBOX) */}
            <div className="mb-4">
              <div
                className="position-relative w-100 rounded-3 overflow-hidden border"
                style={{
                  height: '650px',
                  borderColor: '#cbd5e1',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                }}
              >
                {isLoading && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.75)',
                      zIndex: 15,
                    }}
                  >
                    <div
                      className="spinner-border text-primary mb-2"
                      role="status"
                    ></div>
                    <span className="fw-semibold text-dark small">
                      Loading coverage map data...
                    </span>
                  </div>
                )}

                {/* Floating Banner (Top Left) when City is Selected or Overview */}
                <div
                  className="position-absolute top-0 start-0 m-3 d-flex align-items-center gap-2 bg-white rounded-3 shadow border px-3 py-2"
                  style={{
                    zIndex: 10,
                    maxWidth: selectedOfficerForPopup ? 'calc(100% - 370px)' : '92%',
                    transition: 'max-width 0.2s ease',
                  }}
                >
                  {selectedArea ? (
                    <>
                      <div
                        className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: '28px', height: '28px' }}
                      >
                        <i className="fa-solid fa-city" style={{ fontSize: '12px' }}></i>
                      </div>
                      <div className="me-2 text-truncate" style={{ fontSize: '12.5px' }}>
                        <span className="text-muted">Viewing Officers in </span>
                        <strong className="text-dark">{selectedArea.name}</strong>,{' '}
                        <span className="text-muted">{selectedArea.province_name}</span>{' '}
                        <span className="badge bg-success-subtle text-success border border-success-subtle ms-1">
                          {officersInSelectedArea.length} Officers
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary d-flex align-items-center gap-1.5 flex-shrink-0"
                        style={{
                          borderRadius: '6px',
                          fontSize: '11.5px',
                          padding: '5px 12px',
                          fontWeight: 600,
                        }}
                        onClick={handleBackToAllCities}
                      >
                        <i className="fa-solid fa-arrow-left"></i>
                        Back to All Cities
                      </button>
                    </>
                  ) : (
                    <div
                      className="d-flex align-items-center gap-2 flex-wrap"
                      style={{ fontSize: '12.5px' }}
                    >
                      <i className="fa-solid fa-circle-info text-primary"></i>
                      <span className="text-dark">
                        <strong>Overview Mode:</strong> Double-click any city to view officers.
                      </span>
                    </div>
                  )}
                </div>

                <MapGL
                  ref={mapRef}
                  mapboxAccessToken={MAPBOX_TOKEN}
                  initialViewState={DEFAULT_MAP_VIEW}
                  onMove={(evt) => setCurrentZoom(evt.viewState.zoom)}
                  cursor={isRulerActive ? 'crosshair' : undefined}
                  style={{ width: '100%', height: '100%' }}
                  mapStyle="mapbox://styles/mapbox/streets-v12"
                  onClick={(e) => {
                    if (isRulerActive) {
                      handleRulerPointSelect(Number(e.lngLat.lat), Number(e.lngLat.lng));
                      return;
                    }
                    setSelectedAreaForPopup(null);
                    setSelectedOfficerForPopup(null);
                  }}
                  onMouseMove={(e) => {
                    if (isRulerActive && rulerPointA && !rulerPointB) {
                      setRulerHoverPoint({
                        lat: Number(e.lngLat.lat),
                        lng: Number(e.lngLat.lng),
                      });
                    }
                  }}
                  onMouseLeave={() => {
                    if (isRulerActive && !rulerPointB) {
                      setRulerHoverPoint(null);
                    }
                  }}
                >
                  <NavigationControl position="bottom-left" />

                  {/* 1. OVERVIEW MODE: SHOW CITY MARKERS ONLY WHEN NO CITY IS SELECTED */}
                  {!selectedArea &&
                    filteredAreas.map((area) => {
                      if (!area.latitude || !area.longitude) return null;
                      const isHighlighted = highlightedAreaId === area.id;
                      const isHovered = hoveredAreaId === area.id;

                      // Dynamic stats strictly filtered by active grade pills, province & project
                      const stats = areaFilteredStatsMap.get(area.id.toLowerCase().trim()) || {
                        total: 0,
                        gradeA: 0,
                        gradeB: 0,
                        gradeC: 0,
                        gradeD: 0,
                      };
                      const hasFilteredOfficers = stats.total > 0;

                      // Progressive disclosure:
                      // Tier 1 (< 12.0): Compact pin only
                      // Tier 2 (>= 12.0): City name capsule
                      // Tier 3 (>= 15.0): Full details (total & breakdown)
                      // Hover/click pin shows full details immediately at any zoom level
                      const showName =
                        currentZoom >= SHOW_CITY_NAME_ZOOM ||
                        isHovered ||
                        isHighlighted;

                      const showDetails =
                        currentZoom >= SHOW_CITY_DETAILS_ZOOM ||
                        isHovered ||
                        isHighlighted;

                      return (
                        <Marker
                          key={`area-${area.id}`}
                          latitude={area.latitude}
                          longitude={area.longitude}
                          anchor="bottom"
                          onClick={(e) => {
                            e.originalEvent.stopPropagation();
                            if (isRulerActive) {
                              handleRulerPointSelect(area.latitude, area.longitude);
                              return;
                            }
                            handleAreaClick(area);
                          }}
                        >
                          <div
                            className="d-flex flex-column align-items-center"
                            style={{
                              cursor: isRulerActive ? 'crosshair' : 'pointer',
                              transform:
                                isHighlighted || isHovered
                                  ? 'scale(1.2) translateY(-6px)'
                                  : 'translateY(-2px)',
                              transition: 'all 0.2s ease',
                              zIndex:
                                isHighlighted || isHovered
                                  ? 40
                                  : hasFilteredOfficers
                                    ? 25
                                    : 15,
                            }}
                            onMouseEnter={() => setHoveredAreaId(area.id)}
                            onMouseLeave={() => setHoveredAreaId(null)}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              e.nativeEvent?.stopPropagation();
                              if (isRulerActive) {
                                return;
                              }
                              handleSelectCity(area);
                            }}
                          >
                            {/* PIN ICON: COMPACT PIN WHEN ZOOM < 12.0 AND NOT HOVERED */}
                            {!showName ? (
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white shadow-sm"
                                style={{
                                  width: hasFilteredOfficers ? '22px' : '13px',
                                  height: hasFilteredOfficers ? '22px' : '13px',
                                  backgroundColor: hasFilteredOfficers
                                    ? COLORS.primary
                                    : '#94a3b8',
                                  border: hasFilteredOfficers
                                    ? '2px solid #ffffff'
                                    : '1.5px solid #ffffff',
                                  boxShadow: hasFilteredOfficers
                                    ? '0 2px 6px rgba(43, 117, 218, 0.45)'
                                    : '0 1px 3px rgba(0,0,0,0.2)',
                                  fontSize: '9.5px',
                                  fontWeight: 700,
                                }}
                                title={`${area.name} (Filtered: ${stats.total} Officers)`}
                              >
                                {hasFilteredOfficers ? stats.total : ''}
                              </div>
                            ) : (
                              // EXPANDED PIN DISPLAYING TOTAL OFFICERS COUNT (RETAINING NUMBER INSTEAD OF BUILDING ICON)
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white shadow"
                                style={{
                                  width: hasFilteredOfficers ? '26px' : '18px',
                                  height: hasFilteredOfficers ? '26px' : '18px',
                                  backgroundColor: hasFilteredOfficers
                                    ? COLORS.primary
                                    : '#94a3b8',
                                  border: '2.5px solid #ffffff',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  boxShadow:
                                    isHighlighted || isHovered
                                      ? '0 6px 14px rgba(43, 117, 218, 0.6)'
                                      : '0 2px 6px rgba(0,0,0,0.3)',
                                }}
                                title={`${area.name} (Filtered: ${stats.total} Officers)`}
                              >
                                {hasFilteredOfficers ? stats.total : '0'}
                              </div>
                            )}

                            {/* TIER 2: CITY NAME ONLY CAPSULE (WHEN ZOOM >= 12.0 AND < 15.0 AND NOT HOVERED/HIGHLIGHTED) */}
                            {showName && !showDetails && (
                              <div
                                className="mt-1 shadow-sm text-center fw-bold"
                                style={{
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e2e8f0',
                                  color: '#0f172a',
                                  fontSize: '11px',
                                  padding: '3px 8px',
                                  borderRadius: '20px',
                                  maxWidth: '160px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                }}
                                title={`${area.name} (Filtered: ${stats.total} Officers)`}
                              >
                                {area.name}
                              </div>
                            )}

                            {/* TIER 3: FULL CARD WITH NAME, TOTAL & DYNAMIC GRADE BREAKDOWN (WHEN ZOOM >= 15.0 OR HOVERED/HIGHLIGHTED) */}
                            {showDetails && (
                              <div
                                className="d-flex flex-column align-items-center mt-1 shadow"
                                style={{
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e2e8f0',
                                  boxShadow:
                                    isHighlighted || isHovered
                                      ? '0 8px 24px rgba(0,0,0,0.18)'
                                      : '0 4px 14px rgba(0,0,0,0.12)',
                                  minWidth: '135px',
                                  maxWidth: '200px',
                                  padding: '8px 10px',
                                  borderRadius: '10px',
                                }}
                              >
                                {/* City Name */}
                                <div
                                  className="text-center fw-bold px-1"
                                  style={{
                                    fontSize: '11px',
                                    color: '#0f172a',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: '100%',
                                    marginBottom: '4px',
                                  }}
                                >
                                  {area.name}
                                </div>

                                {/* Total Filtered Officers */}
                                <div
                                  className="w-100 d-flex align-items-center justify-content-center gap-1"
                                  style={{
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #f1f5f9',
                                    borderRadius: '6px',
                                    padding: '3px 8px',
                                    fontSize: '9.5px',
                                    fontWeight: 600,
                                    marginBottom: '5px',
                                  }}
                                >
                                  <span style={{ color: '#64748b' }}>Total:</span>
                                  <span
                                    className="fw-bold"
                                    style={{
                                      color: '#2B75DA',
                                      fontSize: '11px',
                                      marginLeft: '2px',
                                      marginRight: '2px',
                                    }}
                                  >
                                    {stats.total}
                                  </span>
                                  <span
                                    style={{ color: '#94a3b8', fontSize: '8.5px' }}
                                  >
                                    Officers
                                  </span>
                                </div>

                                {/* Dynamic Grade Breakdown (respecting active tab pills) */}
                                <div
                                  className="d-flex align-items-center justify-content-center gap-1 flex-wrap w-100"
                                  style={{ padding: '0 2px' }}
                                >
                                  {selectedGrades.includes('A') && (
                                    <span
                                      style={{
                                        backgroundColor: COLORS.gradeA,
                                        color: '#ffffff',
                                        fontSize: '8.5px',
                                        padding: '2px 5px',
                                        fontWeight: 700,
                                        borderRadius: '4px',
                                      }}
                                      title={`Grade A: ${stats.gradeA}`}
                                    >
                                      A:{stats.gradeA}
                                    </span>
                                  )}
                                  {selectedGrades.includes('B') && (
                                    <span
                                      style={{
                                        backgroundColor: COLORS.gradeB,
                                        color: '#ffffff',
                                        fontSize: '8.5px',
                                        padding: '2px 5px',
                                        fontWeight: 700,
                                        borderRadius: '4px',
                                      }}
                                      title={`Grade B: ${stats.gradeB}`}
                                    >
                                      B:{stats.gradeB}
                                    </span>
                                  )}
                                  {selectedGrades.includes('C') && (
                                    <span
                                      style={{
                                        backgroundColor: COLORS.gradeC,
                                        color: '#ffffff',
                                        fontSize: '8.5px',
                                        padding: '2px 5px',
                                        fontWeight: 700,
                                        borderRadius: '4px',
                                      }}
                                      title={`Grade C: ${stats.gradeC}`}
                                    >
                                      C:{stats.gradeC}
                                    </span>
                                  )}
                                  {selectedGrades.includes('D') && (
                                    <span
                                      style={{
                                        backgroundColor: COLORS.gradeD,
                                        color: '#ffffff',
                                        fontSize: '8.5px',
                                        padding: '2px 5px',
                                        fontWeight: 700,
                                        borderRadius: '4px',
                                      }}
                                      title={`Grade D: ${stats.gradeD}`}
                                    >
                                      D:{stats.gradeD}
                                    </span>
                                  )}
                                </div>

                                {/* Active Projects Indicator if any */}
                                {area.active_projects.length > 0 && (
                                  <div
                                    className="w-100 text-center mt-1 pt-1"
                                    style={{
                                      borderTop: '1px solid #f1f5f9',
                                      fontSize: '8.5px',
                                      color: '#64748b',
                                      letterSpacing: '0.2px',
                                    }}
                                  >
                                    <i className="fa-solid fa-briefcase me-1 text-success"></i>
                                    {area.active_projects.length} Active Projects
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </Marker>
                      );
                    })}

                  {/* 2. DETAIL MODE: SHOW CITY CENTER ANCHOR & FIELD OFFICER MARKERS */}
                  {selectedArea && selectedArea.latitude && selectedArea.longitude && (
                    <Marker
                      latitude={selectedArea.latitude}
                      longitude={selectedArea.longitude}
                      anchor="bottom"
                      onClick={(e) => {
                        e.originalEvent.stopPropagation();
                        if (isRulerActive) {
                          handleRulerPointSelect(selectedArea.latitude, selectedArea.longitude);
                          return;
                        }
                        setSelectedOfficerForPopup(null);
                        setSelectedAreaForPopup(selectedArea);
                      }}
                    >
                      <div
                        className="d-flex flex-column align-items-center"
                        style={{ cursor: isRulerActive ? 'crosshair' : 'pointer', zIndex: 10 }}
                      >
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white shadow"
                          style={{
                            width: '36px',
                            height: '36px',
                            backgroundColor: COLORS.primaryDark,
                            border: '3px solid #ffffff',
                            fontSize: '14px',
                          }}
                        >
                          <i className="fa-solid fa-building"></i>
                        </div>
                        <div
                          className="badge bg-dark text-white shadow-sm mt-1"
                          style={{ fontSize: '10px', padding: '3px 8px' }}
                        >
                          {selectedArea.name} (Center)
                        </div>
                      </div>
                    </Marker>
                  )}

                  {/* OFFICER MARKERS (ONLY WHEN A CITY IS SELECTED) */}
                  {selectedArea &&
                    officersWithDisplayCoords.map((officer) => {
                      const isHighlighted = highlightedOfficerId === officer.id;

                      let pinBg = COLORS.ungraded;
                      const g = officer.grade ? officer.grade.toUpperCase() : '';
                      if (g.includes('GRADE A') || g === 'A') pinBg = COLORS.gradeA;
                      else if (g.includes('GRADE B') || g === 'B') pinBg = COLORS.gradeB;
                      else if (g.includes('GRADE C') || g === 'C') pinBg = COLORS.gradeC;
                      else if (g.includes('GRADE D') || g === 'D') pinBg = COLORS.gradeD;

                      return (
                        <Marker
                          key={`officer-${officer.id}`}
                          latitude={officer.displayLat}
                          longitude={officer.displayLng}
                          anchor="bottom"
                          onClick={(e) => {
                            e.originalEvent.stopPropagation();
                            if (isRulerActive) {
                              handleRulerPointSelect(officer.displayLat, officer.displayLng);
                              return;
                            }
                            setSelectedAreaForPopup(null);
                            setSelectedOfficerForPopup(officer);
                            setHighlightedOfficerId(officer.id);
                            flyToLocation(officer.displayLat, officer.displayLng);
                          }}
                        >
                          <div
                            className="d-flex flex-column align-items-center"
                            style={{
                              cursor: isRulerActive ? 'crosshair' : 'pointer',
                              transform: isHighlighted
                                ? 'scale(1.25) translateY(-6px)'
                                : 'translateY(-2px)',
                              transition: 'all 0.25s ease',
                              zIndex: isHighlighted ? 40 : 25,
                            }}
                          >
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center text-white shadow"
                              style={{
                                width: '28px',
                                height: '28px',
                                backgroundColor: pinBg,
                                border: '2px solid #ffffff',
                                fontSize: '12px',
                                boxShadow: isHighlighted
                                  ? '0 6px 14px rgba(0,0,0,0.4)'
                                  : '0 2px 6px rgba(0,0,0,0.25)',
                              }}
                              title={`${officer.name} (${officer.grade})`}
                            >
                              <i className="fa-solid fa-user"></i>
                            </div>

                            <div
                              className="mt-1 shadow-sm"
                              style={{
                                backgroundColor: 'rgba(15, 23, 42, 0.92)',
                                color: '#ffffff',
                                fontSize: '10px',
                                fontWeight: 600,
                                padding: '3px 9px',
                                borderRadius: '8px',
                                whiteSpace: 'nowrap',
                                maxWidth: '135px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textAlign: 'center',
                                boxShadow: isHighlighted
                                  ? '0 4px 10px rgba(0,0,0,0.4)'
                                  : '0 2px 6px rgba(0,0,0,0.28)',
                                border: '1px solid rgba(255, 255, 255, 0.18)',
                                lineHeight: '1.25',
                                letterSpacing: '0.15px',
                              }}
                              title={officer.name}
                            >
                              {officer.name}
                            </div>
                          </div>
                        </Marker>
                      );
                    })}

                  {/* Area Details Popup */}
                  {selectedAreaForPopup && (() => {
                    const popupStats = areaFilteredStatsMap.get(selectedAreaForPopup.id.toLowerCase().trim()) || {
                      total: 0,
                      gradeA: 0,
                      gradeB: 0,
                      gradeC: 0,
                      gradeD: 0,
                    };

                    return (
                      <Popup
                        latitude={selectedAreaForPopup.latitude}
                        longitude={selectedAreaForPopup.longitude}
                        anchor="top"
                        onClose={() => setSelectedAreaForPopup(null)}
                        closeOnClick={false}
                      >
                        <div style={{ maxWidth: '250px', padding: '6px' }}>
                          <div className="fw-bold text-dark fs-6 mb-0">
                            {selectedAreaForPopup.name}
                          </div>
                          <div className="text-muted small mb-2">
                            Province: {selectedAreaForPopup.province_name || '-'}
                          </div>

                          <div className="border-top pt-2 mb-2">
                            <div className="d-flex justify-content-between small text-muted mb-1">
                              <span>Filtered Officers:</span>
                              <strong className="text-primary">{popupStats.total}</strong>
                            </div>
                            <div className="d-flex justify-content-between small text-muted mb-1">
                              <span>Total in Area:</span>
                              <span className="text-dark fw-medium">
                                {selectedAreaForPopup.total_officers}
                              </span>
                            </div>

                            {/* Dynamic Grade Breakdown */}
                            <div className="fw-semibold small text-dark mt-2 mb-1" style={{ fontSize: '11px' }}>
                              Grade Breakdown (Filtered):
                            </div>
                            <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
                              {selectedGrades.includes('A') && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: COLORS.gradeA,
                                    color: '#fff',
                                    fontSize: '9px',
                                  }}
                                  title={`Grade A: ${popupStats.gradeA}`}
                                >
                                  A: {popupStats.gradeA}
                                </span>
                              )}
                              {selectedGrades.includes('B') && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: COLORS.gradeB,
                                    color: '#fff',
                                    fontSize: '9px',
                                  }}
                                  title={`Grade B: ${popupStats.gradeB}`}
                                >
                                  B: {popupStats.gradeB}
                                </span>
                              )}
                              {selectedGrades.includes('C') && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: COLORS.gradeC,
                                    color: '#fff',
                                    fontSize: '9px',
                                  }}
                                  title={`Grade C: ${popupStats.gradeC}`}
                                >
                                  C: {popupStats.gradeC}
                                </span>
                              )}
                              {selectedGrades.includes('D') && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: COLORS.gradeD,
                                    color: '#fff',
                                    fontSize: '9px',
                                  }}
                                  title={`Grade D: ${popupStats.gradeD}`}
                                >
                                  D: {popupStats.gradeD}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="border-top pt-2 mb-2">
                            <div className="fw-semibold small text-dark mb-1">
                              Active Projects ({selectedAreaForPopup.active_projects.length}):
                            </div>
                            {selectedAreaForPopup.active_projects.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {selectedAreaForPopup.active_projects.map((p) => (
                                  <span
                                    key={p.id}
                                    className="badge bg-primary-subtle text-primary border border-primary-subtle"
                                    style={{ fontSize: '9.5px' }}
                                  >
                                    {p.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-muted small">No active projects</div>
                            )}
                          </div>

                          {/* Button to view officers in this city */}
                          {!selectedArea && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm w-100 mt-2"
                              style={{ fontSize: '11px', borderRadius: '6px' }}
                              onClick={() => handleSelectCity(selectedAreaForPopup)}
                            >
                              <i className="fa-solid fa-users me-1"></i>
                              View Officers in this City &rarr;
                            </button>
                          )}
                        </div>
                      </Popup>
                    );
                  })()}

                  {/* RULER FEATURE (PENGGARIS JARAK A KE B) */}
                  {rulerPointA && activeRulerPointB && (
                    <Source id="ruler-line-source" type="geojson" data={rulerGeoJSON}>
                      <Layer {...rulerLineCasingLayer} />
                      <Layer {...rulerLineLayer} />
                    </Source>
                  )}

                  {/* RULER POINT A MARKER */}
                  {rulerPointA && (
                    <Marker
                      latitude={rulerPointA.lat}
                      longitude={rulerPointA.lng}
                      anchor="center"
                      draggable={true}
                      onDrag={(e) => {
                        setRulerPointA({ lat: Number(e.lngLat.lat), lng: Number(e.lngLat.lng) });
                      }}
                      style={{ zIndex: 60 }}
                    >
                      <div
                        style={{ cursor: 'grab', userSelect: 'none' }}
                        title="Point A (Drag to reposition)"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow"
                          style={{
                            width: '28px',
                            height: '28px',
                            backgroundColor: '#2563eb',
                            border: '2.5px solid #ffffff',
                            fontSize: '12px',
                            boxShadow: '0 3px 10px rgba(37, 99, 235, 0.55)',
                            transition: 'transform 0.15s ease',
                          }}
                        >
                          A
                        </div>
                      </div>
                    </Marker>
                  )}

                  {/* RULER POINT B MARKER (CONFIRMED) */}
                  {rulerPointB && (
                    <Marker
                      latitude={rulerPointB.lat}
                      longitude={rulerPointB.lng}
                      anchor="center"
                      draggable={true}
                      onDrag={(e) => {
                        setRulerPointB({ lat: Number(e.lngLat.lat), lng: Number(e.lngLat.lng) });
                      }}
                      style={{ zIndex: 60 }}
                    >
                      <div
                        style={{ cursor: 'grab', userSelect: 'none' }}
                        title="Point B (Drag to reposition)"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow"
                          style={{
                            width: '28px',
                            height: '28px',
                            backgroundColor: '#10b981',
                            border: '2.5px solid #ffffff',
                            fontSize: '12px',
                            boxShadow: '0 3px 10px rgba(16, 185, 129, 0.55)',
                            transition: 'transform 0.15s ease',
                          }}
                        >
                          B
                        </div>
                      </div>
                    </Marker>
                  )}

                  {/* RULER POINT B MARKER (LIVE HOVER PREVIEW) */}
                  {rulerPointA && !rulerPointB && rulerHoverPoint && (
                    <Marker
                      latitude={rulerHoverPoint.lat}
                      longitude={rulerHoverPoint.lng}
                      anchor="center"
                      style={{ zIndex: 55, pointerEvents: 'none' }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                        style={{
                          width: '24px',
                          height: '24px',
                          backgroundColor: 'rgba(16, 185, 129, 0.8)',
                          border: '2px dashed #ffffff',
                          fontSize: '11px',
                        }}
                      >
                        B
                      </div>
                    </Marker>
                  )}

                  {/* RULER MIDPOINT DISTANCE BADGE */}
                  {rulerPointA && activeRulerPointB && (() => {
                    const midLat = (rulerPointA.lat + activeRulerPointB.lat) / 2;
                    const midLng = (rulerPointA.lng + activeRulerPointB.lng) / 2;
                    const dist = calculateDistanceKm(
                      rulerPointA.lat,
                      rulerPointA.lng,
                      activeRulerPointB.lat,
                      activeRulerPointB.lng
                    );
                    return (
                      <Marker
                        latitude={midLat}
                        longitude={midLng}
                        anchor="center"
                        style={{ zIndex: 65, pointerEvents: 'none' }}
                      >
                        <div
                          className="d-flex align-items-center gap-1.5 px-2.5 py-1 shadow rounded-pill fw-bold"
                          style={{
                            backgroundColor: rulerPointB ? '#0f172a' : 'rgba(15, 23, 42, 0.88)',
                            color: '#ffffff',
                            fontSize: '11.5px',
                            border: '1.5px solid #ffffff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            whiteSpace: 'nowrap',
                            transform: 'translateY(-2px)',
                          }}
                        >
                          <i
                            className="fa-solid fa-ruler-combined text-warning"
                            style={{ fontSize: '10.5px' }}
                          ></i>
                          <span>{formatRulerDistance(dist)}</span>
                          {!rulerPointB && (
                            <span style={{ fontSize: '9.5px', opacity: 0.8, fontWeight: 500 }}>
                              (preview)
                            </span>
                          )}
                        </div>
                      </Marker>
                    );
                  })()}

                </MapGL>

                {/* Floating Ruler Toggle Button (Positioned cleanly 10px above Zoom Controls at bottom-left) */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '144px',
                    left: '10px',
                    zIndex: 10,
                    width: '29px',
                    height: '29px',
                    borderRadius: '4px',
                    backgroundColor: isRulerActive ? '#2B75DA' : '#ffffff',
                    boxShadow: isRulerActive
                      ? '0 0 0 2px rgba(43, 117, 218, 0.4), 0 2px 6px rgba(0,0,0,0.15)'
                      : '0 0 0 2px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden',
                  }}
                >
                  <button
                    type="button"
                    onClick={handleToggleRuler}
                    title={
                      isRulerActive
                        ? 'Deactivate Ruler'
                        : 'Measure Distance (Point A to B)'
                    }
                    style={{
                      width: '29px',
                      height: '29px',
                      minWidth: '29px',
                      maxWidth: '29px',
                      padding: 0,
                      margin: 0,
                      border: 'none',
                      background: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: isRulerActive ? '#ffffff' : '#334155',
                    }}
                  >
                    <i className="fa-solid fa-ruler-combined" style={{ fontSize: '13px' }}></i>
                  </button>
                </div>

                {/* Floating Ruler Status / Action Panel */}
                {isRulerActive && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '143px',
                      left: '47px',
                      zIndex: 10,
                      backgroundColor: '#ffffff',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                      padding: '4px 10px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      maxWidth: selectedOfficerForPopup
                        ? 'calc(100% - 390px)'
                        : 'calc(100% - 60px)',
                      whiteSpace: 'nowrap',
                      height: '31px',
                      boxSizing: 'border-box',
                    }}
                  >
                    {!rulerPointA ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a' }}>
                        <span
                          style={{
                            backgroundColor: '#2563eb',
                            color: '#ffffff',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            fontSize: '10px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          A
                        </span>
                        <span style={{ fontWeight: 500 }}>Click on map to select Point A</span>
                      </div>
                    ) : !rulerPointB ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a' }}>
                        <span
                          style={{
                            backgroundColor: '#10b981',
                            color: '#ffffff',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            fontSize: '10px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          B
                        </span>
                        <span style={{ fontWeight: 500 }}>Click on map to select Point B</span>
                        <button
                          type="button"
                          onClick={handleResetRuler}
                          title="Reset Point A"
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            color: '#475569',
                            fontSize: '11px',
                            fontWeight: 500,
                            padding: '1px 7px',
                            cursor: 'pointer',
                            height: '21px',
                            minWidth: 'auto',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            marginLeft: '4px',
                          }}
                        >
                          <i className="fa-solid fa-rotate-left" style={{ fontSize: '9px' }}></i>
                          <span>Reset</span>
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#64748b' }}>Distance A &rarr; B:</span>
                        <strong style={{ color: '#2563eb', fontSize: '13px' }}>
                          {formatRulerDistance(
                            calculateDistanceKm(
                              rulerPointA.lat,
                              rulerPointA.lng,
                              rulerPointB.lat,
                              rulerPointB.lng
                            )
                          )}
                        </strong>
                        <span style={{ color: '#94a3b8', fontSize: '10.5px' }}>
                          (drag pins to adjust)
                        </span>
                        <button
                          type="button"
                          onClick={handleResetRuler}
                          title="Reset measurement"
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            color: '#475569',
                            fontSize: '11px',
                            fontWeight: 500,
                            padding: '1px 7px',
                            cursor: 'pointer',
                            height: '21px',
                            minWidth: 'auto',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            marginLeft: '2px',
                          }}
                        >
                          <i className="fa-solid fa-rotate-left" style={{ fontSize: '9px' }}></i>
                          <span>Reset</span>
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleToggleRuler}
                      title="Close ruler"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        padding: '0 4px',
                        fontSize: '13px',
                        lineHeight: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 'auto',
                        height: '21px',
                        marginLeft: '2px',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                )}

                {/* Floating Side Panel for Selected Field Officer (Right Side) */}
                {selectedOfficerForPopup && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      bottom: '16px',
                      width: '330px',
                      maxWidth: 'calc(100% - 32px)',
                      zIndex: 12,
                      backgroundColor: '#ffffff',
                      borderRadius: '12px',
                      boxShadow:
                        '0 10px 25px -5px rgba(0, 0, 0, 0.18), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header */}
                    <div
                      className="p-3 border-bottom d-flex align-items-start justify-content-between"
                      style={{ backgroundColor: '#ffffff', borderColor: '#f1f5f9' }}
                    >
                      <div className="d-flex align-items-start gap-2.5" style={{ minWidth: 0 }}>
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white flex-shrink-0"
                          style={{
                            width: '36px',
                            height: '36px',
                            backgroundColor: '#2B75DA',
                            fontSize: '14px',
                            boxShadow: '0 2px 6px rgba(43, 117, 218, 0.3)',
                          }}
                        >
                          <i className="fa-solid fa-user"></i>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="d-flex align-items-center gap-1.5 flex-wrap">
                            <span
                              className="fw-bold text-dark text-truncate"
                              style={{ fontSize: '14px' }}
                              title={selectedOfficerForPopup.name}
                            >
                              {selectedOfficerForPopup.name}
                            </span>
                            {renderGradeBadge(selectedOfficerForPopup.grade)}
                          </div>
                          <div
                            className="text-muted small text-truncate mt-0.5"
                            style={{ fontSize: '11px' }}
                            title={selectedOfficerForPopup.phone || selectedOfficerForPopup.email || '-'}
                          >
                            <i className="fa-solid fa-phone me-1" style={{ fontSize: '10px' }}></i>
                            {selectedOfficerForPopup.phone || selectedOfficerForPopup.email || '-'}
                          </div>
                          <div
                            className="text-muted small text-truncate mt-0.5"
                            style={{ fontSize: '11px' }}
                            title={`${selectedOfficerForPopup.area_name || '-'}, ${selectedOfficerForPopup.province_name || '-'}`}
                          >
                            <i
                              className="fa-solid fa-location-dot me-1 text-danger"
                              style={{ fontSize: '10px' }}
                            ></i>
                            {selectedOfficerForPopup.area_name || '-'},{' '}
                            {selectedOfficerForPopup.province_name || '-'}
                          </div>
                        </div>
                      </div>

                      {/* Close Button */}
                      <button
                        type="button"
                        className="btn btn-sm btn-light border-0 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ms-1"
                        style={{ width: '28px', height: '28px', color: '#64748b' }}
                        onClick={() => setSelectedOfficerForPopup(null)}
                        title="Close panel"
                      >
                        <i className="fa-solid fa-xmark" style={{ fontSize: '13px' }}></i>
                      </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="p-3 flex-grow-1" style={{ overflowY: 'auto' }}>
                      {/* Metric Summary Card */}
                      <div
                        className="p-2.5 rounded-3 mb-3"
                        style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
                      >
                        <div className="row g-2 align-items-center">
                          <div className="col-6 border-end">
                            <div
                              className="text-muted"
                              style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.3px' }}
                            >
                              OVERALL SCORE
                            </div>
                            <div className="fw-bold text-primary fs-5 mt-0.5">
                              {selectedOfficerForPopup.overall_score > 0
                                ? selectedOfficerForPopup.overall_score.toFixed(1)
                                : '-'}
                            </div>
                          </div>
                          <div className="col-6 ps-2">
                            <div
                              className="text-muted"
                              style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.3px' }}
                            >
                              WORKLOAD
                            </div>
                            <div className="mt-1">
                              {selectedOfficerForPopup.active_task_count > 0 ? (
                                <span
                                  className={`badge ${
                                    selectedOfficerForPopup.active_task_count < 10
                                      ? 'bg-warning-subtle text-dark border border-warning-subtle'
                                      : 'bg-primary-subtle text-primary border border-primary-subtle'
                                  }`}
                                  style={{ fontSize: '10px' }}
                                >
                                  <i className="fa-solid fa-list-check me-1"></i>
                                  {selectedOfficerForPopup.active_task_count} Tasks
                                </span>
                              ) : (
                                <span
                                  className="badge bg-light text-muted border"
                                  style={{ fontSize: '10px' }}
                                >
                                  0 Active Tasks
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Assigned Projects */}
                      <div className="mb-3">
                        <div
                          className="fw-semibold text-dark mb-1.5"
                          style={{ fontSize: '12px' }}
                        >
                          Assigned Projects ({selectedOfficerForPopup.active_projects.length}):
                        </div>
                        {selectedOfficerForPopup.active_projects.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1.5">
                            {selectedOfficerForPopup.active_projects.map((p, idx) => {
                              const info = getOfficerProjectInfo(p);
                              return (
                                <span
                                  key={idx}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '3px 4px 3px 10px',
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '20px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: '#334155',
                                    lineHeight: '18px',
                                  }}
                                >
                                  <span>{info.name}</span>
                                  {info.taskCount !== undefined && (
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '18px',
                                        height: '18px',
                                        padding: '0 5px',
                                        borderRadius: '999px',
                                        backgroundColor: '#2B75DA',
                                        color: '#ffffff',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        lineHeight: 1,
                                      }}
                                      title={`${info.taskCount} Active Tasks`}
                                    >
                                      {info.taskCount}
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-muted small fst-italic">
                            No active projects
                          </span>
                        )}
                      </div>

                      {/* ex-Projects with Approved Results */}
                      <div>
                        <div
                          className="fw-semibold text-dark mb-1.5"
                          style={{ fontSize: '12px' }}
                        >
                          ex-Projects ({selectedOfficerForPopup.ex_projects?.length || 0}):
                        </div>
                        {selectedOfficerForPopup.ex_projects &&
                        selectedOfficerForPopup.ex_projects.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1.5">
                            {selectedOfficerForPopup.ex_projects.map((p, idx) => {
                              const isSelected = selectedExProjects.includes(p.id);
                              return (
                                <span
                                  key={idx}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    padding: '3px 8px',
                                    backgroundColor: isSelected ? '#eff6ff' : '#f8fafc',
                                    border: isSelected
                                      ? '1.5px solid #2563eb'
                                      : '1px solid #cbd5e1',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: isSelected ? 700 : 500,
                                    color: isSelected ? '#1d4ed8' : '#334155',
                                  }}
                                  title={`${p.name} (${p.approved_count || 1} approved result)`}
                                >
                                  <span>{p.name}</span>
                                  {p.approved_count !== undefined && (
                                    <span
                                      style={{
                                        fontSize: '9.5px',
                                        fontWeight: 600,
                                        color: isSelected ? '#2563eb' : '#94a3b8',
                                      }}
                                    >
                                      ✓{p.approved_count}
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-muted small fst-italic">
                            No past projects with approved results
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Footer Action Button */}
                    <div
                      className="p-3 border-top"
                      style={{ backgroundColor: '#f8fafc', borderColor: '#f1f5f9' }}
                    >
                      <button
                        type="button"
                        className="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-1.5"
                        style={{
                          borderRadius: '8px',
                          fontSize: '12px',
                          padding: '8px 12px',
                          fontWeight: 600,
                          boxShadow: '0 2px 5px rgba(43, 117, 218, 0.28)',
                        }}
                        onClick={() =>
                          handleOpenUserDetailModal(selectedOfficerForPopup.id)
                        }
                      >
                        <i className="fa-solid fa-eye me-1"></i>
                        View Detail Field Officer
                      </button>
                    </div>
                  </div>
                )}


              </div>
            </div>

            {/* SECTION 2: FIELD OFFICERS TABLE (FULL WIDTH) */}
            <div className="row g-4">
              <div className="col-12">
                <div
                  className="border rounded-3 p-3 bg-white h-100 d-flex flex-column"
                  style={{ borderColor: '#e2e8f0' }}
                >
                  {/* Table Header & Search */}
                  <div className="d-flex justify-content-between align-items-center mb-2.5 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center"
                        style={{ width: '28px', height: '28px' }}
                      >
                        <i
                          className="fa-solid fa-users"
                          style={{ fontSize: '13px' }}
                        ></i>
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold text-dark">
                          {selectedArea ? (
                            <>
                              Officers in {selectedArea.name}{' '}
                              <span className="text-primary">
                                ({tableOfficers.length})
                              </span>
                            </>
                          ) : (
                            <>
                              Field Officers List{' '}
                              <span className="text-primary">
                                ({tableOfficers.length})
                              </span>
                            </>
                          )}
                        </h6>
                      </div>

                      {/* Pill button to view all officers if currently filtered to an area */}
                      {selectedArea && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm py-0 px-2"
                          style={{ fontSize: '11px', borderRadius: '12px' }}
                          onClick={handleBackToAllCities}
                          title="Show officers from all cities"
                        >
                          Show All Cities
                        </button>
                      )}
                    </div>

                    <div style={{ width: '200px' }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search name, email, or phone..."
                        value={officerSearchTerm}
                        onChange={(e) => {
                          setOfficerSearchTerm(e.target.value);
                          setOfficerPage(1);
                        }}
                      />
                    </div>
                  </div>

                  {/* Officers Table */}
                  <div
                    className="table-responsive flex-grow-1"
                    style={{ minHeight: '380px' }}
                  >
                    <table
                      className="table table-sm table-hover align-middle mb-0"
                      style={{ fontSize: '12.5px' }}
                    >
                      <thead className="table-primary">
                        <tr>
                          <th>Officer Name</th>
                          <th>Area (City & Province)</th>
                          <th style={{ width: '105px' }}>Grade & Score</th>
                          <th>Workload & Projects</th>
                          <th style={{ width: '68px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan={5} className="text-center py-4 text-muted">
                              <div
                                className="spinner-border spinner-border-sm text-primary me-2"
                                role="status"
                              ></div>
                              Loading officer data...
                            </td>
                          </tr>
                        ) : paginatedOfficers.length > 0 ? (
                          paginatedOfficers.map((officer) => {
                            const isHighlighted = highlightedOfficerId === officer.id;

                            return (
                              <tr
                                key={officer.id}
                                className={isHighlighted ? 'table-secondary' : ''}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  setHighlightedOfficerId(officer.id);
                                  setSelectedOfficerForPopup(officer);
                                  // If area is not selected yet, auto-select this officer's city
                                  if (!selectedArea && officer.area_id) {
                                    const matchingArea = areas.find(
                                      (a) => a.id === officer.area_id
                                    );
                                    if (matchingArea) {
                                      setSelectedArea(matchingArea);
                                    }
                                  }
                                  flyToLocation(
                                    officer.latitude,
                                    officer.longitude,
                                    12.5
                                  );
                                }}
                              >
                                <td>
                                  <div className="fw-semibold text-dark">
                                    {officer.name}
                                  </div>
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: '10.5px' }}
                                  >
                                    {officer.phone || officer.email || '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-dark fw-medium">
                                    {officer.area_name || '-'}
                                  </div>
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: '10.5px' }}
                                  >
                                    {officer.province_name || '-'}
                                  </div>
                                </td>
                                <td>
                                  <div>{renderGradeBadge(officer.grade)}</div>
                                  {officer.overall_score > 0 && (
                                    <div
                                      className="text-muted mt-0.5"
                                      style={{ fontSize: '10px' }}
                                    >
                                      Score:{' '}
                                      <strong>
                                        {officer.overall_score.toFixed(1)}
                                      </strong>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex flex-column gap-1">
                                    <div className="d-flex align-items-center gap-1.5 flex-wrap">
                                      {officer.active_task_count > 0 ? (
                                        <span
                                          className={`badge ${officer.active_task_count < 10
                                            ? 'bg-warning-subtle text-dark border border-warning-subtle'
                                            : 'bg-primary-subtle text-primary border border-primary-subtle'
                                            }`}
                                          style={{ fontSize: '10px' }}
                                        >
                                          <i className="fa-solid fa-list-check me-1"></i>
                                          {officer.active_task_count} Active Tasks
                                        </span>
                                      ) : (
                                        <span
                                          className="badge bg-light text-muted border"
                                          style={{ fontSize: '10px' }}
                                        >
                                          0 Active Tasks
                                        </span>
                                      )}
                                      {!officer.is_assigned && officer.active_projects.length === 0 && (
                                        <span
                                          className="badge bg-success-subtle text-success border border-success-subtle"
                                          style={{ fontSize: '9.5px' }}
                                        >
                                          Available
                                        </span>
                                      )}
                                    </div>
                                    {officer.active_projects.length > 0 && (
                                      <div className="d-flex flex-wrap gap-1 mt-0.5">
                                        {officer.active_projects.map((p, idx) => {
                                          const info = getOfficerProjectInfo(p);
                                          return (
                                            <span
                                              key={idx}
                                              style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                padding: '2px 3px 2px 7px',
                                                backgroundColor: '#f8fafc',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '20px',
                                                fontSize: '10.5px',
                                                fontWeight: 600,
                                                color: '#334155',
                                                lineHeight: '16px',
                                              }}
                                            >
                                              <span>{info.name}</span>
                                              {info.taskCount !== undefined && (
                                                <span
                                                  style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minWidth: '16px',
                                                    height: '16px',
                                                    padding: '0 4px',
                                                    borderRadius: '999px',
                                                    backgroundColor: '#2B75DA',
                                                    color: '#ffffff',
                                                    fontSize: '9.5px',
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                  }}
                                                  title={`${info.taskCount} Active Tasks`}
                                                >
                                                  {info.taskCount}
                                                </span>
                                              )}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                    {officer.ex_projects && officer.ex_projects.length > 0 && (
                                      <div className="d-flex flex-wrap gap-1 mt-1 align-items-center">
                                        <span
                                          className="text-muted fw-semibold"
                                          style={{ fontSize: '9px', letterSpacing: '0.2px' }}
                                        >
                                          ex:
                                        </span>
                                        {officer.ex_projects.map((p, idx) => {
                                          const isSelected = selectedExProjects.includes(p.id);
                                          return (
                                            <span
                                              key={idx}
                                              style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '3px',
                                                padding: '1px 6px',
                                                backgroundColor: isSelected ? '#eff6ff' : '#f8fafc',
                                                border: isSelected ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                                                borderRadius: '10px',
                                                fontSize: '9.5px',
                                                fontWeight: isSelected ? 700 : 500,
                                                color: isSelected ? '#1d4ed8' : '#64748b',
                                                lineHeight: '14px',
                                              }}
                                              title={`${p.name} (${p.approved_count || 1} approved result)`}
                                            >
                                              <span>{p.name}</span>
                                              {p.approved_count !== undefined && (
                                                <span
                                                  style={{
                                                    fontSize: '8.5px',
                                                    color: isSelected ? '#2563eb' : '#94a3b8',
                                                  }}
                                                >
                                                  ✓{p.approved_count}
                                                </span>
                                              )}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-info p-1 border-0"
                                      title="View Detail Field Officer"
                                      style={{ width: '28px', height: '28px' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenUserDetailModal(officer.id);
                                      }}
                                    >
                                      <i className="fa-solid fa-eye"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary p-1 border-0"
                                      title="Focus Officer on Map"
                                      style={{ width: '28px', height: '28px' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setHighlightedOfficerId(officer.id);
                                        setSelectedOfficerForPopup(officer);
                                        if (!selectedArea && officer.area_id) {
                                          const matchingArea = areas.find(
                                            (a) => a.id === officer.area_id
                                          );
                                          if (matchingArea) {
                                            setSelectedArea(matchingArea);
                                          }
                                        }
                                        flyToLocation(
                                          officer.latitude,
                                          officer.longitude,
                                          12.5
                                        );
                                      }}
                                    >
                                      <i className="fa-solid fa-location-crosshairs"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center py-4 text-muted"
                            >
                              No officer data matches the filter.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Officer Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                    <div className="d-flex align-items-center">
                      <span
                        className="me-2 text-muted"
                        style={{ fontSize: '11.5px' }}
                      >
                        Rows:
                      </span>
                      <CustomDropdown
                        value={officerPageSize}
                        options={PageSizeOption.options}
                        onChange={(opt) => {
                          setOfficerPageSize(Number(opt.id));
                          setOfficerPage(1);
                        }}
                        align="top"
                        buttonStyle={{
                          height: 32,
                          padding: '2px 8px',
                          fontSize: '11.5px',
                        }}
                      />
                    </div>

                    <span
                      className="text-muted"
                      style={{ fontSize: '11.5px' }}
                    >
                      {totalOfficerRecords > 0
                        ? `${(officerPage - 1) * officerPageSize + 1}–${Math.min(
                          officerPage * officerPageSize,
                          totalOfficerRecords
                        )} of ${totalOfficerRecords}`
                        : '0 of 0'}
                    </span>

                    <nav>
                      <ul className="mb-0 pagination pagination-sm">
                        <li
                          className={`page-item ${officerPage === 1 ? 'disabled' : ''
                            }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setOfficerPage(officerPage - 1)}
                            style={{ border: 0 }}
                          >
                            <i className="fa fa-angle-left"></i>
                          </button>
                        </li>
                        <li className="page-item disabled">
                          <span
                            className="page-link fw-bold text-primary"
                            style={{ border: 0 }}
                          >
                            {officerPage}/{totalOfficerPages}
                          </span>
                        </li>
                        <li
                          className={`page-item ${officerPage === totalOfficerPages ? 'disabled' : ''
                            }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setOfficerPage(officerPage + 1)}
                            style={{ border: 0 }}
                          >
                            <i className="fa fa-angle-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Reused UserDetailModal from Field Officer */}
      <UserDetailModal
        show={showUserDetailModal}
        onClose={handleCloseUserDetailModal}
        userId={selectedOfficerForModal || undefined}
        token={token}
      />
    </div>
  );
};

export default CoverageAnalysis;
